import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { STALE_TIME } from 'infrastructure/consts/stale-time';
import { arrUniqEl } from 'infrastructure/functions';
import { BaseSelect } from 'infrastructure/classes/select/base-select';

import useApi from '../use-api';

import type {
  IPaginationMeta,
  IPartialPaginationMeta,
  IPaginatedCustomerTypes,
} from 'infrastructure/interfaces';

const api = 'core-2/customer-types/select-options';

export interface IUseCustomerTypeMeta {
  search?: string;
  guid?: string;
}

type MergedMeta = IUseCustomerTypeMeta & IPaginationMeta;

interface UseResults {
  loading: boolean;
  meta: MergedMeta;
  loadCustomerTypes: (meta: MergedMeta) => Promise<BaseSelect[]>;
}

interface IProps {
  meta?: IUseCustomerTypeMeta & IPartialPaginationMeta;
}

export const useCustomerTypeSelect = (props?: IProps): UseResults => {
  const { meta: metaProps = {} } = props || {};
  const { loading, loadData, isMounted } = useApi();
  const queryClient = useQueryClient();

  const [meta, setMeta] = useState<MergedMeta>({
    page: 1,
    items: 100,
    totalCount: 0,
    ...metaProps,
  });

  const loadCustomerTypes = async (filters: MergedMeta) => {
    const queryKey = [
      api,
      {
        page: filters.page,
        search: filters.search,
        guid: filters.guid,
      },
    ];

    const data = await queryClient.fetchQuery(
      queryKey,
      () =>
        loadData<
          IPaginatedCustomerTypes,
          Omit<IUseCustomerTypeMeta, 'sortField'>
        >(api, {
          sortField: JSON.stringify(filters.sortField),
          page: filters.page,
          items: filters.items,
          guid: filters.guid,
          search: filters.search,
        }),
      { staleTime: STALE_TIME },
    );

    if (isMounted && data) {
      const newMeta = {
        ...meta,
        ...filters,
        totalCount: data.count,
      };

      setMeta(newMeta);

      return arrUniqEl(data.items, 'guid').map((el) => new BaseSelect(el));
    }

    return [];
  };

  return {
    loading,
    meta,
    loadCustomerTypes,
  };
};
